import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { useTranslate } from "../../../../i18n/useTranslationModules";
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";

import { Session, SessionPreview } from "../../../../types/working-model";
import { niceDate, niceDateCalendar, niceTwoDigitTime, toDictTyped } from "../../../../utils/utils";
import EventsCalendar from "../../../general-ui/events-calendar/events-calendar";
import SessionType from "../../../general-ui/session-type/session-type";
import { ParamsProps } from "../../live-event";
import SessionTags from "./session-tags";

interface IAgendaCalendar {
	sessions: (Session | SessionPreview)[];
}

const AgendaCalendar: React.FC<IAgendaCalendar> = ({ sessions = [] }) => {
	const { language } = useParams<ParamsProps>();
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const event = workingEvent ?? eventBundle;

	const { t } = useTranslate(['landing', 'homepage']);

	const sessionsByDate = useCallback(() => {
		const _sessionsByDate: Record<string, (Session | SessionPreview)[]> = {};
		(sessions || []).forEach(session => {
			if (session.timestamp && session.end_timestamp) {
				const date = niceDate(session.timestamp);
				if (!_sessionsByDate[date]) {
					_sessionsByDate[date] = [session];
				} else {
					_sessionsByDate[date].push(session);
				}
			}
		});
		return _sessionsByDate;
	}, [sessions]);

	const renderStartAndEndTime = useCallback((event) => {
		if (!event.timestamp || !event.end_timestamp) return null;

		let endDay = '';
		if (event.end_timestamp_display !== event.end_timestamp) {
			endDay = ' (' + new Date(event.end_timestamp).toLocaleDateString(language || navigator.language, { weekday: 'long', month: 'long', day: '2-digit', year: 'numeric' }) + ')';
		}

		const startTime = niceTwoDigitTime(event.timestamp);
		const endTime = niceTwoDigitTime(event.end_timestamp);
		return `${startTime} - ${endTime}${endDay}`;
	}, [language]);

	const tagMap = useMemo(() => toDictTyped('uuid', event?.tags ?? []), [event?.tags]);

	return (
		<div className="agenda-calendar-container">
			{Object.entries(sessionsByDate()).map((data) => {
				const [date, sessions] = data;
				return (
					<div key={date} className="agenda-calendar-block-container">
						<h5 className="agenda-calendar-block-date">{niceDateCalendar(date, language)}</h5>
						<EventsCalendar
							uniqueKey="uuid"
							sessions={sessions}
							renderCard={({ session }) => {
								return (
									<div className="calendar-session-card-content">
										<div className="calendar-session-card-time">{renderStartAndEndTime(session)} • <SessionType sessionType={session?.session_type} casing="upper" /> {session?.tracks?.length ? '• ' : ''} {session?.tracks?.join(', ')}</div>
										<h4>{t(`agenda.session.${session.session}.title`, session.title[language] as string || session.title.base || '')}</h4>
										<div className="calendar-view-session-tags">
											<SessionTags tags={session?.tags?.map((t: string) => tagMap[t])?.filter((t: string) => !!t)} className="calendar-view" />
										</div>
									</div>
								);
							}}
						/>
					</div>
				);
			})}
		</div>
	);
};

export default AgendaCalendar;
